import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaTitleAfterDeathPage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Title Transfer After Death</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Vehicle Title Transfer After Death</h1>
        <p className="text-obsidian-300">How to transfer a vehicle title in Nevada when the owner has passed away.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Transfer Without Probate (Small Estates)</h2>
          <p>Nevada allows vehicle title transfers without going through the full probate process for small estates. <strong>Form VP 024 (Transfer of Estates Without Probate)</strong> can be used when the total value of the estate is below the threshold set by Nevada law.</p>
          <div className="mt-4 bg-obsidian-50 rounded-xl p-5">
            <p className="font-semibold text-obsidian-900 text-sm mb-2">Required Documents for VP 024:</p>
            <ul className="space-y-2">
              {[
                'Completed Form VP 024 (Transfer of Estates Without Probate)',
                'Death certificate of the deceased owner',
                'Original vehicle title',
                'Proof of relationship to the deceased (if applicable)',
                'Affidavit stating the estate qualifies for simplified transfer',
              ].map(item => (
                <li key={item} className="flex items-start space-x-2 text-sm text-obsidian-700">
                  <i className="ri-checkbox-circle-line text-green-500 flex-shrink-0 mt-0.5"></i><span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Joint Ownership Situations</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'Joint Tenants with Right of Survivorship', desc: 'The surviving owner automatically inherits the vehicle. Provide death certificate and title to DMV.' },
              { title: '"OR" on Title', desc: 'Either owner could sign. Surviving owner can transfer with death certificate.' },
              { title: '"AND" on Title', desc: 'Both signatures required. Surviving owner needs death certificate and may need probate.' },
              { title: 'Community Property', desc: 'Nevada is a community property state. Surviving spouse may have rights to the vehicle.' },
            ].map(item => (
              <div key={item.title} className="border border-obsidian-100 rounded-lg p-4">
                <p className="font-semibold text-obsidian-900 text-sm mb-1">{item.title}</p>
                <p className="text-obsidian-500 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">When Probate Is Required</h2>
          <p className="text-sm">If the estate exceeds the small estate threshold or the title situation is complex, full probate may be required. In this case, the court-appointed executor or administrator will handle the title transfer with Letters Testamentary or Letters of Administration.</p>
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-amber-800 text-xs"><strong>Note:</strong> Speedy DMV Services is not an attorney and cannot provide legal advice on estate matters. Consult a Nevada probate attorney for complex situations.</p>
          </div>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Need Help With a Title Transfer?</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services can assist with vehicle title transfers in Nevada, including estate situations.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaTitleAfterDeathPage;
